import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FileArrowUp, Check, WarningCircle } from "@phosphor-icons/react";
import { importFile } from "@/lib/api";
import { toast } from "sonner";

const ACCEPTED = ".csv,.xlsx,.xlsm,.xls";

export default function ImportDialog({ open, onOpenChange, projectId, onImported }) {
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInput = useRef(null);

  const reset = () => { setFile(null); setReplace(false); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file first"); return; }
    setImporting(true);
    try {
      const res = await importFile(projectId, file, replace);
      setResult(res);
      if (res.created > 0) {
        toast.success(`Imported ${res.created} task${res.created === 1 ? "" : "s"}`);
        onImported?.();
      } else {
        toast.warning("No rows imported — check column headers");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="rounded-md max-w-xl" data-testid="import-dialog">
        <DialogHeader>
          <div className="h-11 w-11 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center mb-2">
            <FileArrowUp size={20} weight="bold" />
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Import tasks</DialogTitle>
          <DialogDescription className="text-slate-500">
            Upload a <span className="font-mono-ibm">.csv</span>, <span className="font-mono-ibm">.xlsx</span>, or <span className="font-mono-ibm">.xlsm</span> file.
            Headers must match one of the recognized names.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 py-1">
          <div
            onClick={() => fileInput.current?.click()}
            className={`border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${
              file ? "border-emerald-400 bg-emerald-50/40" : "border-slate-300 hover:border-slate-500 bg-slate-50"
            }`}
            data-testid="import-dropzone"
          >
            <FileArrowUp size={22} weight="bold" className="mx-auto text-slate-500 mb-2" />
            {file ? (
              <>
                <div className="text-sm font-semibold text-slate-950">{file.name}</div>
                <div className="text-[11px] text-slate-500 font-mono-ibm mt-1">{Math.round(file.size / 1024)} KB</div>
              </>
            ) : (
              <>
                <div className="text-sm font-semibold text-slate-950">Click to choose a file</div>
                <div className="text-[11px] text-slate-500 mt-1">.csv · .xlsx · .xlsm</div>
              </>
            )}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED}
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            className="hidden"
            data-testid="import-file-input"
          />

          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={replace}
              onChange={(e) => setReplace(e.target.checked)}
              className="h-4 w-4 rounded-md border-slate-400"
              data-testid="import-replace-checkbox"
            />
            Replace existing tasks (delete all current tasks before importing)
          </label>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 leading-relaxed">
            <div className="font-semibold text-slate-950 mb-1">Recognized headers</div>
            <div><span className="font-mono-ibm">Task</span>, <span className="font-mono-ibm">Subtask</span>, <span className="font-mono-ibm">Workstream</span> / Department, <span className="font-mono-ibm">Status</span>, <span className="font-mono-ibm">Dependencies</span></div>
            <div className="mt-1"><span className="font-mono-ibm">Start</span>, <span className="font-mono-ibm">Decision Made</span>, <span className="font-mono-ibm">Due Date</span>, <span className="font-mono-ibm">Done Date</span></div>
          </div>

          {result && (
            <div className={`rounded-md border p-3 text-xs ${result.created > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="import-result">
              <div className="flex items-center gap-2 font-semibold">
                {result.created > 0 ? <Check size={12} weight="bold" /> : <WarningCircle size={12} weight="bold" />}
                {result.created} task{result.created === 1 ? "" : "s"} imported
              </div>
              {Object.keys(result.mapping || {}).length > 0 && (
                <div className="mt-2 font-mono-ibm text-[11px] leading-relaxed">
                  Mapped: {Object.entries(result.mapping).map(([k, v]) => `${k} → ${v}`).join(", ")}
                </div>
              )}
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 text-[11px] text-slate-700">
                  {result.errors.length} row error{result.errors.length === 1 ? "" : "s"}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-md border-slate-300">
              {result ? "Close" : "Cancel"}
            </Button>
            <Button type="submit" disabled={!file || importing} className="rounded-md bg-slate-950 hover:bg-slate-800 text-white" data-testid="import-submit">
              {importing ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
